import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import * as navStyles from '../../styles/layout/care-navigation.module.scss';
import IconContainer from '../shared/IconContainer';
import MenuItem from './MenuItem';
import PropTypes from 'prop-types';
import Image from '../shared/Image';
import { useTranslation } from 'react-i18next';
import PolyLink from '../shared/PolyLink';
import Modal from 'react-modal';
import SearchPopUp from './SearchPopUp';
import * as theme from '../../styles/theme-care-climate.module.scss';

Modal.setAppElement('body');

const CareNavigation = ({menu, siteURL, logo, locale, websiteRoot}) => {
  const {t} = useTranslation();

  const [currentPath, setCurrentPath] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentPath(window.location.pathname);
      setPrevScrollPos(window.scrollY);
      setIsScrolled(window.scrollY > 0);
    }
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }, [currentPath]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleRouteChange = () => {
        setCurrentPath(window.location.pathname);
        setIsMobileMenuOpen(false);
        setActiveSubmenu(null);
        if (typeof document !== 'undefined') {
          document.body.style.overflow = '';
        }
      };

      window.addEventListener('popstate', handleRouteChange);

      return () => {
        window.removeEventListener('popstate', handleRouteChange);
      };
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        const currentScrollPos = window.scrollY;
        const isScrollingDown = prevScrollPos < currentScrollPos;
        const isAtTop = currentScrollPos < 50;

        if (!isMobileMenuOpen) {
          setVisible((isAtTop || !isScrollingDown));
        }
        setPrevScrollPos(currentScrollPos);
        setIsScrolled(currentScrollPos > 0);
      };

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [prevScrollPos, isMobileMenuOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isSearchOpen]);

  const toggleSubmenu = (title) => {
    setActiveSubmenu(activeSubmenu === title ? null : title);
  };

  const findActiveSubmenu = () => {
    for (const item of transformedMenu) {
      if (item.items) {
        for (const subItem of item.items) {
          if (currentPath === subItem.link) {
            return item.title;
          }
        }
      }
    }
    return null;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden';
      }
      setVisible(true);
      const activeMenu = findActiveSubmenu();
      if (activeMenu) {
        setActiveSubmenu(activeMenu);
      }
    } else {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
      setActiveSubmenu(null);
      const isAtTop = window.scrollY < 50;
      if (!isAtTop) {
        setVisible(false);
      }
    }
  };

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchOpen();
    }
  };

  const getURL = (target, n1_slug, n2_slug) => {
    if (!target || target.type === 'menu') {
      return '';
    }

    const urlParts = [];

    if (target.type === 'strapi') {
      urlParts.push(siteURL.replace(/\/$/, ''));
      if (n1_slug)
        urlParts.push(n1_slug);
      if (n2_slug)
        urlParts.push(n2_slug);
      urlParts.push(target.slug);
      return (urlParts.join('/').replace(/\/+/g, '/').replace(/\/?$/, '/'));
    }

    if (target.type === 'strapi_events' || target.type === 'strapi_news' || target.type === 'strapi_person') {
      urlParts.push(siteURL.replace(/\/$/, ''));
      urlParts.push(target.slug);
      return (urlParts.join('/').replace(/\/+/g, '/').replace(/\/?$/, '/'));
    }

    if (target.type === 'external_link') {
      return (target.uid.replace(/\/?$/, '/'));
    }

    return '';
  };

  const isMenuItemActive = (item) => {
    const url = item.link;
    if (currentPath === url && currentPath !== '') {
      return true;
    }
    if (item.target_object?.type === 'menu' && item.target_object.menu) {
      return item.target_object.menu.some(subItem => {
        const subUrl = getURL(subItem.target_object, item.slug, subItem.slug);
        return currentPath === subUrl;
      });
    }
    return false;
  };

  const transformMenu = (menuItems, parentSlugs = []) => {
    return menuItems
      .filter(item => item.visible)
      .map(item => {
        const currentSlugs = [...parentSlugs];
        if (item.slug) {
          currentSlugs.push(item.slug);
        }

        return {
          title: item.label,
          link: getURL(
            item.target_object,
            currentSlugs.length > 1 ? currentSlugs[0] : null,
            currentSlugs.length > 2 ? currentSlugs[1] : null
          ),
          items: item.target_object?.menu?.length > 0 ? transformMenu(item.target_object.menu, currentSlugs) : null
        };
      });
  };

  const transformedMenu = transformMenu(menu[0]?.items || []);

  return (
    <>
      <Modal
        aria-labelledby="modal-modal-title"
        className={navStyles.modal}
        isOpen={isSearchOpen}
        overlayClassName={clsx(navStyles.overlay, theme.themeCareClimate)}
      >
        <SearchPopUp
          handleClose={handleSearchClose}
          language={locale}
          websiteRoot={websiteRoot}
        />
      </Modal>
      <nav className={clsx(navStyles.careNavigation, {
        [navStyles.hidden]: !visible && !isMobileMenuOpen,
        [navStyles.scrolled]: isScrolled,
        [navStyles.menuActive]: isMobileMenuOpen
      })}
      >
        <div className={navStyles.navigationContainer}>
          <div className={navStyles.logoContainer}>
            <PolyLink url={logo?.navigationPath}>
              <Image
                ariaLabel={t('header.logo')}
                className={navStyles.careLogo}
                image={logo}
              />
            </PolyLink>
          </div>
          <button
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle menu"
            className={clsx(navStyles.mobileMenuToggle)}
            onClick={toggleMobileMenu}
          >
            <IconContainer
              color={'BLACK'}
              icon={isMobileMenuOpen ? 'MENU_CLOSE' : 'MENU_BURGER'}
              noHover
              notFocus
              size={30}
            />
          </button>
          <nav
            className={clsx(navStyles.menuContainer, {
              [navStyles.mobileMenuOpen]: isMobileMenuOpen
            })}
          >
            <ul className={navStyles.menu}>
              {transformedMenu.map((item, index) => (
                <MenuItem
                  activeSubmenu={activeSubmenu}
                  currentPath={currentPath}
                  isActive={isMenuItemActive(transformedMenu[index])}
                  item={item}
                  key={index}
                  toggleSubmenu={toggleSubmenu}
                />
              ))}
              <li className={navStyles.searchMenuItem}>
                <button
                  aria-label={t('search.window.open')}
                  className={navStyles.searchButton}
                  onClick={handleSearchOpen}
                  onKeyDown={handleSearchKeyDown}
                  tabIndex={0}
                  title={t('header.search')}
                >
                  <IconContainer
                    color={'WHITE'}
                    icon="SEARCH"
                    noHover
                    notFocus
                    size={25}
                    tabIndex={-1}
                  />
                  <span className={navStyles.searchButtonText}>{t('common.form.search')}</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </nav>
    </>
  );
};

CareNavigation.propTypes = {
  locale: PropTypes.string,
  logo: PropTypes.object,
  menu: PropTypes.arrayOf(PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      visible: PropTypes.bool,
      target_object: PropTypes.shape({
        type: PropTypes.string,
        menu: PropTypes.array
      })
    }))
  })),
  siteURL: PropTypes.string,
  websiteRoot: PropTypes.string
};

export default CareNavigation;